import type { TFunction } from 'i18next';
import type {
  PickerSuggestionsItemWithStats,
  PickerSuggestionsResponse
} from '@bliks-io/contracts';
import type { BucketRowMeta, BucketSection } from './EntityPickerBucketsList';

interface BucketTitleConfig {
  byRoleTitle?: (roleName: string | null) => string;
  byRoleSubtitle?: (peerCount: number) => string;
  byProcessTitle: (processName: string | null) => string;
  byProcessSubtitle: (sampleSize: number) => string;
  byDepartmentTitle?: (departmentName: string | null) => string;
  byDepartmentSubtitle?: (peerCount: number) => string;
  byTypeTitle?: (type: string) => string;
}

interface BucketBuilderArgs<T extends { _id: string }> {
  byId: Map<string, T>;
  suggestions: PickerSuggestionsResponse['data'] | undefined;
  t: TFunction;
  recentlyUsedLimit?: number;
  config: BucketTitleConfig;
}

/**
 * Walk the suggestions response and produce bucket sections that consume
 * entities from `byId`. Items appear once across buckets in priority order:
 * recentlyUsed → role → department → process → type. Returns the built
 * sections (already filtered to non-empty by caller using `.items`).
 */
export function buildBucketSections<T extends { _id: string }>({
  byId,
  suggestions,
  t,
  recentlyUsedLimit = 5,
  config
}: BucketBuilderArgs<T>): BucketSection<T>[] {
  if (!suggestions) return [];
  const seen = new Set<string>();

  const collect = <I extends { _id: string }>(
    ids: I[] | undefined
  ): { items: T[]; idToItem: Map<string, I> } => {
    const items: T[] = [];
    const idToItem = new Map<string, I>();
    if (!ids) return { items, idToItem };
    for (const idItem of ids) {
      const id = idItem._id;
      if (seen.has(id)) continue;
      const entity = byId.get(id);
      if (!entity) continue;
      items.push(entity);
      idToItem.set(id, idItem);
      seen.add(id);
    }
    return { items, idToItem };
  };

  const sections: BucketSection<T>[] = [];

  const recent = collect(suggestions.recentlyUsed);
  sections.push({
    key: 'recentlyUsed',
    title: t('entityPicker.common.buckets.recentlyUsed'),
    subtitle: t('entityPicker.common.buckets.recentlyUsedSubtitle', {
      count: Math.min(recent.items.length, recentlyUsedLimit)
    }),
    items: recent.items
  });

  if (suggestions.role && config.byRoleTitle && config.byRoleSubtitle) {
    const bucket = collect<PickerSuggestionsItemWithStats>(suggestions.role.items);
    sections.push({
      key: 'role',
      title: config.byRoleTitle(suggestions.role.roleName),
      subtitle: config.byRoleSubtitle(suggestions.role.peerCount),
      items: bucket.items,
      metaFor: (item) => makeMeta(bucket.idToItem.get(item._id))
    });
  }

  if (suggestions.department && config.byDepartmentTitle && config.byDepartmentSubtitle) {
    const bucket = collect<PickerSuggestionsItemWithStats>(suggestions.department.items);
    sections.push({
      key: 'department',
      title: config.byDepartmentTitle(suggestions.department.departmentName),
      subtitle: config.byDepartmentSubtitle(suggestions.department.peerCount),
      items: bucket.items,
      metaFor: (item) => makeMeta(bucket.idToItem.get(item._id))
    });
  }

  if (suggestions.process) {
    const bucket = collect<PickerSuggestionsItemWithStats>(suggestions.process.items);
    sections.push({
      key: 'process',
      title: config.byProcessTitle(suggestions.process.processName),
      subtitle: config.byProcessSubtitle(suggestions.process.sampleSize),
      items: bucket.items,
      metaFor: (item) => makeMeta(bucket.idToItem.get(item._id))
    });
  }

  if (suggestions.type && config.byTypeTitle) {
    const bucket = collect<PickerSuggestionsItemWithStats>(suggestions.type.items);
    sections.push({
      key: 'type',
      title: config.byTypeTitle(suggestions.type.type),
      items: bucket.items,
      metaFor: (item) => makeMeta(bucket.idToItem.get(item._id))
    });
  }

  return sections;
}

/**
 * Builds a per-row meta lookup so the active-query view can show stats and
 * the recommended badge on the same items the bucket view highlighted.
 *
 * Priority: process bucket wins (it carries the recommended flag), then
 * role/department, falling back to type. Recently-used items have no stats.
 */
export function buildRowMetaIndex(
  suggestions: PickerSuggestionsResponse['data'] | undefined
): Map<string, BucketRowMeta> {
  const map = new Map<string, BucketRowMeta>();
  if (!suggestions) return map;
  const accumulate = (items: PickerSuggestionsItemWithStats[]) => {
    for (const item of items) {
      const existing = map.get(item._id);
      if (existing && !item.recommended) continue;
      map.set(item._id, {
        recommended: item.recommended,
        stats: { occurrences: item.occurrences, samples: item.samples }
      });
    }
  };
  if (suggestions.process) accumulate(suggestions.process.items);
  if (suggestions.role) accumulate(suggestions.role.items);
  if (suggestions.department) accumulate(suggestions.department.items);
  if (suggestions.type) accumulate(suggestions.type.items);
  return map;
}

/**
 * The suggestion id-lists that become buckets, in the order (and with the
 * category set) `buildBucketSections` emits them. Single source of truth for
 * "which categories count as bucket content" so the availability check below
 * can't drift from what actually renders — add a bucket there, add it here.
 */
function bucketSuggestionIdSources(
  suggestions: PickerSuggestionsResponse['data'] | undefined
): ReadonlyArray<ReadonlyArray<{ _id: string }>> {
  if (!suggestions) return [];
  const sources: Array<ReadonlyArray<{ _id: string }>> = [];
  if (suggestions.recentlyUsed) sources.push(suggestions.recentlyUsed);
  if (suggestions.role) sources.push(suggestions.role.items);
  if (suggestions.department) sources.push(suggestions.department.items);
  if (suggestions.process) sources.push(suggestions.process.items);
  if (suggestions.type) sources.push(suggestions.type.items);
  return sources;
}

/**
 * Whether the picker should open in the curated bucket view for these candidate
 * items (already `excludeIds`-filtered). Drives the "skip the empty
 * recommendation screen and open the list directly" behaviour.
 *
 * - While the suggestions query is still loading (`undefined`), stay optimistic
 *   and keep the bucket view: the common case has recommendations, so flashing
 *   the all-list and snapping back once they arrive is worse than a brief wait.
 * - Once loaded, only suggestion / pending rows that survive the `byId` lookup
 *   count — mirroring `buildBucketSections` + `EntityPickerBucketsList`'s
 *   empty-section drop — so a recommendation that is excluded or no longer in
 *   the list does NOT hold the bucket view open (it would render blank).
 */
export function shouldShowBucketView<T extends { _id: string }>(
  suggestions: PickerSuggestionsResponse['data'] | undefined,
  candidateItems: T[],
  pendingItems: ReadonlyArray<{ _id: string }> = []
): boolean {
  if (!suggestions) return true;
  const present = new Set(candidateItems.map((i) => i._id));
  if (pendingItems.some((p) => present.has(p._id))) return true;
  return bucketSuggestionIdSources(suggestions).some((source) =>
    source.some((item) => present.has(item._id))
  );
}

function makeMeta(item: PickerSuggestionsItemWithStats | undefined): BucketRowMeta {
  if (!item) return {};
  return {
    recommended: item.recommended,
    stats: { occurrences: item.occurrences, samples: item.samples }
  };
}
